<template lang="pug">
  .yz-radio(:class="{ 'yz-radio--disabled': disabled, 'yz-radio--invalid': computedInvalid }")
    label.yz-radio__container
      input.yz-radio__input(:id="`radio-${_uid}`" type="radio" :value="value" :checked="isChecked" :name="computedName" :disabled="disabled" @change="onChange" @focus="onFocus" @blur="onBlur" ref="radio")
      .yz-radio__button
        .yz-radio__dot
      span.yz-radio__label(v-if="label || $slots.label" :style="{ marginLeft: labelGap }" :class="labelClass")
        slot(name="label") {{ label }}
</template>

<script>
export default {
  name: 'YzRadio',
  inject: {
    radioGroup: {
      from: 'radioGroup',
      default: null
    }
  },
  props: {
    value: {
      type: [String, Number, Boolean, Object],
      required: true
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    },
    labelGap: {
      type: String,
      default: '8px'
    },
    labelClass: {
      type: String,
      default: 'yz-regular-body-sm'
    }
  },
  computed: {
    isChecked() {
      return this.radioGroup ? this.radioGroup.value === this.value : false;
    },
    computedName() {
      return this.radioGroup ? `radio-group-${this.radioGroup._uid}` : `radio-${this._uid}`;
    },
    computedInvalid() {
      return this.invalid || (this.radioGroup && this.radioGroup.hasError);
    }
  },
  methods: {
    onChange() {
      if (!this.disabled) {
        if (this.radioGroup) {
          this.radioGroup.updateValue(this.value);
        } else {
          this.$emit('input', this.value);
          this.$emit('change', this.value);
        }
      }
    },
    onFocus(event) {
      this.$emit('focus', event);
    },
    onBlur(event) {
      this.$emit('blur', event);
    },
    focus() {
      this.$refs.radio.focus();
    },
    blur() {
      this.$refs.radio.blur();
    }
  }
}
</script>

<style lang="scss" scoped>
.yz-radio {
  position: relative;
  display: inline-flex;
  align-items: center;
  font-family: $body-font-family;

  &__container {
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    &:hover .yz-radio__button {
      border-color: var(--yz-neutral-500);
    }
  }

  &__input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;

    &:checked ~ .yz-radio__button {
      border-color: transparent;
      background-color: var(--yz-neutral-700);

      .yz-radio__dot {
        transform: scale(1);
        opacity: 1;
      }
    }

    &:focus ~ .yz-radio__button {
      box-shadow: 0 0 0 2px var(--yz-neutral-300);
    }
  }

  &__button {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 1px solid var(--yz-neutral-400);
    border-radius: 50%;
    background-color: var(--yz-neutral-300);
    transition: all 0.2s ease;
    box-sizing: border-box;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--yz-neutral-100);
    transform: scale(0);
    opacity: 0;
    transition: all 0.2s ease;
  }

  &__label {
    color: var(--yz-neutral-700);
  }

  &--disabled {
    .yz-radio__container {
      cursor: not-allowed;
    }

    .yz-radio__button {
      background-color: var(--yz-neutral-200);
      border-color: var(--yz-neutral-400);
    }

    .yz-radio__container:hover .yz-radio__button {
      border-color: var(--yz-neutral-400);
    }

    .yz-radio__input:checked ~ .yz-radio__button {
      border-color: transparent;
      background-color: var(--yz-neutral-500);
    }

    .yz-radio__label {
      color: var(--yz-neutral-500);
    }
  }

  &--invalid {
    .yz-radio__button {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);
    }

    .yz-radio__container:hover .yz-radio__button {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);
    }

    .yz-radio__input:checked ~ .yz-radio__button {
      background-color: var(--yz-neutral-100);
      border-color: var(--yz-danger);

      .yz-radio__dot {
        background-color: var(--yz-danger);
      }
    }
  }
}
</style>
